import React from "react";
import { Box, Button, Text } from "@mantine/core";
import type { SalonService } from "../../../../../types/salon";
import { useSalon } from "../../../../../context/SalonContext";
import { DESIGN_ICONS } from "../../../../../theme/designIcons";
import type { WaitlistStore } from "../../waitlist/useWaitlist";
import { useWaitlistActions } from "../../waitlist/useWaitlistActions";
import { useStaffAvailability } from "../../waitlist/useStaffAvailability";
import type { WalkInFormTarget } from "../../waitlist/WalkInForm";
import WaitlistList from "../../waitlist/WaitlistList";
import AssignPicker from "../../waitlist/AssignPicker";
import { qualifiedFor } from "../../waitlist/staffAvailability";
import WalkInLink from "../../waitlist/WalkInLink";
import {
  entryClientName,
  type WaitlistToast,
} from "../../waitlist/waitlistModel";
import { MobileSheet } from "../sheets/MobileSheet";
import { MobileWalkInScreen } from "./MobileWalkInScreen";
import styles from "../../waitlist/Waitlist.module.css";

/** What CalendarView hands the phone surface for walk-ins. */
export interface MobileWalkInsWiring {
  store: WaitlistStore;
  /** A walk-in was booked: put its appointment on the grid. */
  onAssigned: (transactionUuid: string) => void;
}

export interface MobileWalkInsProps {
  store: WaitlistStore;
  onAssigned: (transactionUuid: string) => void;
  timezone: string;
  services: SalonService[];
  /** The waitlist bottom sheet (header Walk-ins icon). */
  sheetOpen: boolean;
  onSheetClose: () => void;
  /** New / Edit walk-in screen (null = closed); owned by the surface. */
  formTarget: WalkInFormTarget | null;
  onFormTargetChange: (target: WalkInFormTarget | null) => void;
  /** The surface's MobileToast. */
  onToast: (copy: WaitlistToast) => void;
}

/**
 * The phone's walk-in surfaces over the shared waitlist store (design
 * handoff "Shell (mobile)" + §8): the waitlist as a bottom sheet (touch
 * press-and-hold drag, ⋯ Edit / Move up / Move down / Remove, Assign staff,
 * New Walk-In) and the New / Edit walk-in form as a full-screen push (the
 * FAB's Walk-in opens it directly, without the sheet).
 */
export const MobileWalkIns: React.FC<MobileWalkInsProps> = ({
  store,
  onAssigned,
  timezone,
  services,
  sheetOpen,
  onSheetClose,
  formTarget,
  onFormTargetChange,
  onToast,
}) => {
  const availability = useStaffAvailability(timezone);
  const { rows, nowMs, nowWall } = availability;
  const { refreshServices } = useSalon() as {
    refreshServices?: () => Promise<unknown>;
  };
  const actions = useWaitlistActions(store, onToast, {
    timezone,
    services,
    onAssigned,
    refreshServices,
  });
  const closeForm = () => onFormTargetChange(null);
  const { entries } = store;
  const serviceOf = (id: string) => services.find((s) => s.id === id);

  return (
    <>
      <MobileSheet
        opened={sheetOpen}
        onClose={onSheetClose}
        title="Walk-ins"
        header={
          <Box className={styles.headText}>
            <h2 className={styles.title}>
              Walk-ins
              {entries.length > 0 && (
                <span className={styles.countBadge}>{entries.length}</span>
              )}
              <WalkInLink />
            </h2>
          </Box>
        }
        data-testid="mobile-waitlist-sheet"
      >
        <Box className={`${styles.sheet} ${styles.sheetBody}`}>
          {store.loaded || entries.length > 0 ? (
            <WaitlistList
              variant="sheet"
              entries={entries}
              timezone={timezone}
              services={services}
              now={nowMs}
              onReorder={(ids) => void actions.reorder(ids)}
              onRemove={(entry) => void actions.remove(entry)}
              onEdit={(entry) => onFormTargetChange({ mode: "edit", entry })}
              renderActions={(entry, slot) => (
                <AssignPicker
                  variant="sheet"
                  client={entryClientName(entry)}
                  rows={qualifiedFor(
                    rows,
                    serviceOf(entry.salon_service_uuid),
                    nowWall,
                  )}
                  requestedId={entry.preferred_staff_id}
                  onOpenChange={slot.onOpenChange}
                  onAssign={(choice, mode) =>
                    actions.assign(entry, choice, mode, availability)
                  }
                />
              )}
            />
          ) : (
            <Text size="sm" c="dimmed">
              Loading…
            </Text>
          )}
          <Box className={styles.sheetFooter}>
            <Button
              fullWidth
              size="md"
              h={44}
              leftSection={<DESIGN_ICONS.Plus size={17} />}
              onClick={() => onFormTargetChange({ mode: "new" })}
            >
              New Walk-In
            </Button>
          </Box>
        </Box>
      </MobileSheet>

      <MobileWalkInScreen
        target={formTarget}
        services={services}
        availability={{ rows, nowMs, nowWall, timezone }}
        onClose={closeForm}
        onSave={async (values) => {
          if (!formTarget) return false;
          const saved = await actions.save(formTarget, values, availability);
          if (saved) closeForm();
          return saved;
        }}
      />
    </>
  );
};
